/**
 * Assessments List Page
 * 
 * Shows all assessments with status filtering and search.
 * Entry point to the Assessment → Care Plan workflow.
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Plus,
  Search,
  ClipboardList,
  FileCheck,
  Clock,
  UserCheck,
  AlertCircle,
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_CONFIG = {
  new_referral: { label: "New Referral", color: "bg-blue-500" },
  pending_assessment: { label: "Pending Assessment", color: "bg-blue-400" },
  assessment_in_progress: { label: "In Progress", color: "bg-yellow-500" },
  assessment_complete: { label: "Complete", color: "bg-yellow-600" },
  ai_draft_care_plan: { label: "AI Draft", color: "bg-purple-500" },
  pending_review: { label: "Pending Review", color: "bg-orange-500" },
  approved: { label: "Approved", color: "bg-green-500" },
  needs_caregiver_assignment: { label: "Needs Assignment", color: "bg-orange-400" },
  caregiver_assigned: { label: "Assigned", color: "bg-green-400" },
  ready_for_scheduling: { label: "Ready to Schedule", color: "bg-green-500" },
  active_client: { label: "Active", color: "bg-green-600" },
  on_hold: { label: "On Hold", color: "bg-gray-500" },
  discharged: { label: "Discharged", color: "bg-gray-400" },
};

export default function AssessmentsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [statuses, setStatuses] = useState([]);

  // Fetch assessments
  const fetchAssessments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") {
        params.append("status_filter", statusFilter);
      }
      if (search) {
        params.append("search", search);
      }
      
      const response = await axios.get(`${API}/api/assessments/?${params.toString()}`, {
        withCredentials: true,
      });
      setAssessments(response.data);
    } catch (err) {
      console.error("Failed to fetch assessments:", err);
      setError("Failed to load assessments");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  // Fetch statuses
  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/api/assessments/statuses`, {
        withCredentials: true,
      });
      setStatuses(response.data);
    } catch (err) {
      console.error("Failed to fetch statuses:", err);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    fetchAssessments();
  }, [fetchAssessments]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAssessments();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fetchAssessments]);

  // Create new assessment
  const handleCreateAssessment = async () => {
    try {
      const response = await axios.post(
        `${API}/api/assessments/`,
        {},
        { withCredentials: true }
      );
      navigate(`/assessments/${response.data.id}`);
    } catch (err) {
      console.error("Failed to create assessment:", err);
      setError("Failed to create assessment");
    }
  };

  // Calculate stats
  const stats = {
    new_referrals: assessments.filter(a => a.status === "new_referral").length,
    in_progress: assessments.filter(a => a.status === "assessment_in_progress").length,
    pending_review: assessments.filter(a => a.status === "pending_review").length,
    active_clients: assessments.filter(a => a.status === "active_client").length,
  };

  // Check if user can create assessments
  const canCreateAssessment = ["platform_owner", "agency_admin", "hr_staff", "assessor"].includes(user?.role);

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Assessments</h1>
            <p className="text-gray-500 mt-1">
              Client assessments and care plan management
            </p>
          </div>
          {canCreateAssessment && (
            <Button
              onClick={handleCreateAssessment}
              className="bg-primary hover:bg-primary/90"
              data-testid="new-assessment-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Assessment
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.new_referrals}</p>
                <p className="text-sm text-gray-500">New Referrals</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.in_progress}</p>
                <p className="text-sm text-gray-500">In Progress</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.pending_review}</p>
                <p className="text-sm text-gray-500">Pending Review</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active_clients}</p>
                <p className="text-sm text-gray-500">Active Clients</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by client name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {statuses.map((status) => (
                    <SelectItem key={status.key} value={status.key}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg">
            {error}
          </div>
        )}

        {/* Assessments Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Assessments</CardTitle>
            <CardDescription>
              Click on an assessment to view details or continue the evaluation
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : assessments.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">
                  No assessments found
                </h3>
                <p className="text-gray-500 mb-4">
                  {search || statusFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Get started by creating your first assessment"}
                </p>
                {canCreateAssessment && !search && statusFilter === "all" && (
                  <Button onClick={handleCreateAssessment}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Assessment
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Referral Date</TableHead>
                      <TableHead>Assessor</TableHead>
                      <TableHead>Completion</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assessments.map((assessment) => {
                      const statusConfig = STATUS_CONFIG[assessment.status] || {
                        label: assessment.status,
                        color: "bg-gray-500",
                      };
                      return (
                        <TableRow
                          key={assessment.id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => navigate(`/assessments/${assessment.id}`)}
                          data-testid={`assessment-row-${assessment.id}`}
                        >
                          <TableCell className="font-medium">
                            {assessment.client_name || (
                              <span className="text-gray-400 italic">
                                No name yet
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={`${statusConfig.color} text-white hover:${statusConfig.color}`}
                            >
                              {statusConfig.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {assessment.referral_date
                              ? new Date(assessment.referral_date).toLocaleDateString()
                              : "-"}
                          </TableCell>
                          <TableCell>
                            {assessment.assessor_name || "-"}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all"
                                  style={{
                                    width: `${assessment.completion_percentage}%`,
                                  }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-500">
                                {assessment.completion_percentage}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-500">
                            {new Date(assessment.updated_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
